import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { useTranslate } from 'react-admin';
import { Palette } from '@/themes/themeOverrides/Palette';
import { differenceInDays, startOfDay, isBefore, isAfter } from 'date-fns';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';

const StyledServiceDate = styled(Box)(() => ({
    fontWeight: '700',
    [`@media (max-width: ${breakpoints.values.lg}px)`]: {
        padding: '8px 0',
    },
    '& .device-status-field-time': {
        fontSize: '11px',
        fontWeight: '400',
        position: 'relative',
        top: '6px',
        [`@media (max-width: ${breakpoints.values.lg}px)`]: {
            top: '0px',
        },
    },
}));

const StyledServiceDateType = styled(Box)(() => ({
    fontSize: '11px',
    color: Palette.black,
    borderRadius: '4px',
    lineHeight: '16px',
    padding: '0 6px',
    display: 'inline-block',
    verticalAlign: 'top',
    marginTop: '1px',
    marginLeft: '8px',
    fontWeight: '400',
    letterSpacing: '-0.14px',
    '&.service-date-type--warranty': {
        backgroundColor: Palette.yellow.yellow500,
    },
    '&.service-date-type--postWarranty': {
        backgroundColor: Palette.gray.gray300,
    },
}));

const ServiceDate = ({ formatSince, type = '', className, map = false, mapLabel = '' }) => {
    const translate = useTranslate();

    const date = new Date(formatSince);
    const today = startOfDay(new Date());
    const normalizedDate = startOfDay(date);
    const isPastDate = isBefore(normalizedDate, today);
    const isFutureDate = isAfter(normalizedDate, today);

    let daysStrPast;
    if (isPastDate) {
        const diffPast = differenceInDays(today, normalizedDate);
        daysStrPast =
            diffPast === 1 ? `${diffPast} ${translate('analytics.common.daySingular')}` : translate('analytics.common.daysWithCount', { count: diffPast });
    }
    let daysStrFuture;
    if (isFutureDate) {
        const diffFuture = differenceInDays(normalizedDate, today);
        daysStrFuture =
            diffFuture === 1
                ? `${diffFuture} ${translate('analytics.common.daySingular')}`
                : translate('analytics.common.daysWithCount', { count: diffFuture });
    }

    const typeElement = type ? (
        <StyledServiceDateType className={`service-date-type service-date-type--${type}`}>
            {type === 'warranty' ? translate('resources.serviceInspection.labels.warranty') : translate('resources.serviceInspection.labels.postWarranty')}
        </StyledServiceDateType>
    ) : null;

    return (
        <StyledServiceDate className={className} sx={{ color: isPastDate ? Palette.red.red900 : Palette.black }}>
            <Box sx={{ whiteSpace: 'nowrap' }}>
                {map ? <span className='map-date-field-label'>{mapLabel}</span> : ''}
                {date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })} {typeElement}
            </Box>
            <Box className='device-status-field-time'>
                {isPastDate && translate('resources.serviceInspection.labels.afterTerm', { days: daysStrPast })}
                {isFutureDate && translate('resources.serviceInspection.labels.daysLeft', { days: daysStrFuture })}
            </Box>
        </StyledServiceDate>
    );
};

ServiceDate.propTypes = {
    type: PropTypes.oneOf(['warranty', 'postWarranty']),
    formatSince: PropTypes.string.isRequired,
    className: PropTypes.string,
    map: PropTypes.bool,
    mapLabel: PropTypes.string,
};

export default ServiceDate;
